// eslint-disable-next-line import/no-cycle
import levelManagementService from '@/services/levelManagementService';

export default {
  state: {
    levelInfo: [],
    totalXP: 0,
    currentXP: 0,
    currentLevel: '',
    levelCompleted: false,
    levelUpPopupShown: false,
    // Queue of level-up steps so we can show multiple (e.g. 2→3, 3→4, 4→5) one by one
    levelUpQueue: [],
    // Legacy: single old/new for backward compat when queue not used
    levelUpOldLevel: null,
    levelUpNewLevel: null,
    // Inactivity warning (banner on home)
    inactivityThresholdDays: 14,
    daysSinceLastProgress: null,
    /** Home dashboard (ranks, weekly XP, streak, leaderboard) from GET /user-level-progress/dashboard; null if not loaded or error */
    levelDashboard: null,
    /** Fortschritt page payload from GET /user-level-progress/progress-overview */
    levelProgressOverview: null,
  },
  getters: {
    isInactive: (state) => (
      state.daysSinceLastProgress != null
      && state.inactivityThresholdDays != null
      && state.daysSinceLastProgress >= state.inactivityThresholdDays
    ),
    getLevelInfo: (state) => state.levelInfo,
    getTotalXP: (state) => state.totalXP,
    getCurrentXP: (state) => state.currentXP,
    getUserLevel: (state) => state.currentLevel,
    getLevelCompleted: (state) => state.levelCompleted,
    getLevelUpPopupShown: (state) => state.levelUpPopupShown &&
      (state.levelUpQueue.length > 0
      || (state.levelUpOldLevel != null && state.levelUpNewLevel != null)),
    getLevelUpOldLevel: (state) => (state.levelUpQueue.length > 0
      ? state.levelUpQueue[0].oldLevel : state.levelUpOldLevel),
    getLevelUpNewLevel: (state) => (state.levelUpQueue.length > 0
      ? state.levelUpQueue[0].newLevel : state.levelUpNewLevel),
    getLevelDashboard: (state) => state.levelDashboard,
    getLevelProgressOverview: (state) => state.levelProgressOverview,
  },
  mutations: {
    setLevelInfo(state, data) {
      state.levelInfo = data;
    },
    setTotalXP(state, data) {
      state.totalXP = data;
    },
    setCurrentXP(state, data) {
      state.currentXP = data;
    },
    setCurrentLevel(state, data) {
      state.currentLevel = data;
    },
    setLevelCompleted(state, data) {
      state.levelCompleted = data;
    },
    updateCompletedStatus(state, id) {
      state.levelInfo = state.levelInfo.map((obj) => {
        if (obj.id === id) {
          // eslint-disable-next-line no-param-reassign
          obj.completed = true;
          state.currentXP += obj.xp;
        }
        return obj;
      });
    },
    updateLevelUpPopup(state) {
      state.levelUpPopupShown = !state.levelUpPopupShown;
    },
    showLevelUpPopup(state, { oldLevel, newLevel, levelUpSteps } = {}) {
      state.levelUpPopupShown = true;
      if (levelUpSteps && Array.isArray(levelUpSteps) && levelUpSteps.length > 0) {
        state.levelUpQueue = levelUpSteps
          .map((s) => ({ oldLevel: s.oldLevel, newLevel: s.newLevel }));
        state.levelUpOldLevel = null;
        state.levelUpNewLevel = null;
      } else if (oldLevel !== undefined && newLevel !== undefined) {
        state.levelUpQueue = [];
        state.levelUpOldLevel = oldLevel;
        state.levelUpNewLevel = newLevel;
      }
    },
    advanceLevelUpQueue(state) {
      if (state.levelUpQueue.length > 0) {
        state.levelUpQueue = state.levelUpQueue.slice(1);
      }
      if (state.levelUpQueue.length === 0) {
        state.levelUpPopupShown = false;
        state.levelUpOldLevel = null;
        state.levelUpNewLevel = null;
      }
    },
    hideLevelUpPopup(state) {
      state.levelUpPopupShown = false;
      state.levelUpQueue = [];
      state.levelUpOldLevel = null;
      state.levelUpNewLevel = null;
    },
    setInactivityThresholdDays(state, value) {
      state.inactivityThresholdDays = value;
    },
    setDaysSinceLastProgress(state, value) {
      state.daysSinceLastProgress = value;
    },
    setLevelDashboard(state, payload) {
      state.levelDashboard = payload;
    },
    setLevelProgressOverview(state, payload) {
      state.levelProgressOverview = payload;
    },
  },
  actions: {
    async fetchInactivityState({ commit }) {
      let summary = null;
      let config = null;
      try {
        summary = await levelManagementService.getMySummary();
      } catch (e) {
        commit('setDaysSinceLastProgress', null);
        return;
      }
      try {
        config = await levelManagementService.getLevelSystemConfig();
      } catch {
        // use default threshold if config fails (e.g. non-admin)
      }
      const threshold = config?.inactivityThresholdDays ?? 14;
      commit('setInactivityThresholdDays', threshold);
      commit('setDaysSinceLastProgress', summary?.daysSinceLastProgress ?? null);
    },
    async fetchLevelDashboard({ commit }) {
      try {
        const data = await levelManagementService.getDashboard();
        commit('setLevelDashboard', data);
      } catch {
        commit('setLevelDashboard', null);
      }
    },
    async fetchLevelProgressOverview({ commit }) {
      try {
        const data = await levelManagementService.getProgressOverview();
        commit('setLevelProgressOverview', data);
      } catch {
        commit('setLevelProgressOverview', null);
      }
    },
    async getCurrentLevelInfo({ commit }) {
      // add API call to fetch user's level information from backend
      const data = await levelManagementService.getSpecificLevel();
      if (data.length !== 0) {
        commit('setLevelInfo', data.goals);
        commit('setTotalXP', data.totalXP);
        commit('setCurrentXP', data.currentXP);
        commit('setCurrentLevel', data.level);
        commit('setLevelCompleted', data.completed);
        // Note: This action doesn't show the popup, but if it did, we'd pass oldLevel/newLevel
      }
    },
    async updateGoalStatus({ commit }, { goalId, progress }) {
      const data = await levelManagementService.updateGoalStatus(goalId, progress);

      commit('setTotalXP', data.totalXP);
      commit('setCurrentXP', data.currentXP);
      commit('setCurrentLevel', data.level);
      commit('setLevelInfo', data.goals);
      if (data.completed && data.showLevelUpPopup !== false) {
        // Show popup(s): current level-up and any already-completed next levels (levelUpSteps)
        if (data.levelUpSteps && data.levelUpSteps.length > 0) {
          commit('showLevelUpPopup', { levelUpSteps: data.levelUpSteps });
        } else {
          const oldLevel = data.level;
          const newLevel = data.level + 1;
          commit('showLevelUpPopup', { oldLevel, newLevel });
        }
      } else {
        commit('setLevelCompleted', data.completed);
      }
    },
  },
};
